import { useState } from 'react'
import { Stack } from '@mui/material'
import {
    EarSlashIcon,
    GlobeHemisphereEastIcon,
    HourglassLowIcon,
    LockSimpleIcon,
    QuestionIcon,
    ShieldCheckIcon,
} from '@phosphor-icons/react'
import { Modal } from 'ComponentLibrary/src/piazza/modals'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { ClankerCheckImage } from 'ComponentLibrary/index'

export type AiDataPrivacyInfoTranslations = {
    tooltip: string
    label: string
    modalTitle: string
    closeButtonLabel: string
    items: {
        confidentialData: string
        storageDuration: string
        euProcessing: string
        noModelTraining: string
        supportPrefix: string
        supportLinkLabel: string
    }
}

type AiDataPrivacyInfoProps = {
    onModalOpen?: () => void
    translations?: AiDataPrivacyInfoTranslations
}

const defaultTranslations: AiDataPrivacyInfoTranslations = {
    tooltip:
        'Ihre Daten werden innerhalb der EU verarbeitet und nicht zum Training von KI-Modellen verwendet. Klicken Sie für weitere Informationen.',
    label: 'Datenschutzkonform',
    modalTitle: 'Datenschutz',
    closeButtonLabel: 'Schließen',
    items: {
        confidentialData: 'Wir verarbeiten Ihre Daten vertraulich und geben Sie nicht an Dritte weiter.',
        storageDuration:
            'Hochgeladene Dateien werden vorübergehend für die KI-Verarbeitung gespeichert und innerhalb eines Tages gelöscht.',
        euProcessing: 'einwert nutzt ausschließlich europäische Server. Ihre Daten verlassen nie die EU.',
        noModelTraining: 'Ihre Daten werden nicht zum Training von KI-Modellen verwendet.',
        supportPrefix: 'Schreiben Sie uns bei Fragen gerne an',
        supportLinkLabel: 'support@einwert.com',
    },
}

export function AiDataPrivacyInfo({ onModalOpen, translations = defaultTranslations }: AiDataPrivacyInfoProps) {
    const [isOpen, setIsOpen] = useState(false)
    const closeModal = () => setIsOpen(false)
    const openModal = () => {
        onModalOpen?.()
        setIsOpen(true)
    }

    return (
        <>
            <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="center">
                <TinyButton
                    tooltip={translations.tooltip}
                    label={translations.label}
                    onClick={openModal}
                    leftIcon={<ShieldCheckIcon color={tokens.ColorIconAccentLeaf} />}
                />
            </Stack>

            <Modal
                title={translations.modalTitle}
                isOpen={isOpen}
                onClose={closeModal}
                showCloseIcon
                closeIconTooltip={translations.closeButtonLabel}
                width="620px"
                footerProps={{ secondaryButton: { text: translations.closeButtonLabel, onClick: closeModal } }}
            >
                <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                    <Stack alignItems="center" justifyContent="center" bgcolor={tokens.ColorGraphSequentialFiveLightest} width="100%">
                        <ClankerCheckImage height="224px" width="auto" />
                    </Stack>
                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <LockSimpleIcon
                                size={tokens.SizingIconS}
                                weight="bold"
                                color={tokens.ColorIconDefault}
                                style={{ flexShrink: 0 }}
                            />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {translations.items.confidentialData}
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <HourglassLowIcon
                                size={tokens.SizingIconS}
                                weight="bold"
                                color={tokens.ColorIconDefault}
                                style={{ flexShrink: 0 }}
                            />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {translations.items.storageDuration}
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <GlobeHemisphereEastIcon
                                size={tokens.SizingIconS}
                                weight="bold"
                                color={tokens.ColorIconDefault}
                                style={{ flexShrink: 0 }}
                            />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {translations.items.euProcessing}
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <EarSlashIcon
                                size={tokens.SizingIconS}
                                weight="bold"
                                color={tokens.ColorIconDefault}
                                style={{ flexShrink: 0 }}
                            />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {translations.items.noModelTraining}
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing075}>
                            <QuestionIcon
                                size={tokens.SizingIconS}
                                weight="bold"
                                color={tokens.ColorIconDefault}
                                style={{ flexShrink: 0 }}
                            />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {translations.items.supportPrefix}{' '}
                                <a style={{ color: tokens.ColorGraphSequentialTwoDarkest }} href="mailto:support@einwert.com">
                                    {translations.items.supportLinkLabel}
                                </a>
                                .
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Modal>
        </>
    )
}
